import React, { Fragment, useState, useCallback, useEffect } from 'react';
import { useTranslate } from 'react-admin';
import { useSelector, useDispatch } from 'react-redux';
import { makeStyles } from '@material-ui/core/styles';
import { DateTimePicker } from '@material-ui/pickers';
import { time } from '~/utils';

import { filterChange, selectors, cleanupFilters } from '~/ducks/virtualizedList';
import { Moment } from '~/types';
import { debounce } from 'lodash';
import { l } from '../BankAccountList';
import BackgroundTaskExport from '~/components/BackgroundTaskExport';
import { hasCurrentLocale12HourFormat } from '~/utils/moment';

interface Props {
  resource: string;
  record: any;
}

const Filters = (props: Props) => {
  const { resource, record } = props;

  const filters = useSelector(selectors.getFilters<{ [x: string]: any }>(resource));
  const [localValues, setLocalValues] = useState(filters);
  useEffect(() => {
    setLocalValues({ ...filters });
  }, [filters]);

  const classes = useStyles();
  const dispatch = useDispatch();
  const translate = useTranslate();
  const t = (key: string): string => translate(l(`transactionsHistory.${key}`));

  const dispatchFilterChange = (name: string, value: string | Moment) => {
    dispatch(filterChange({ resource, filters: { [name]: value || null } }));
  };
  const debouncedDispatchFilterChange = useCallback(debounce(dispatchFilterChange, 1000), []);

  const handleChange = (name: string) => (eventOrData: any) => {
    switch (name) {
      case 'fromTimestamp':
      case 'toTimestamp': {
        setLocalValues((prev) => ({
          ...prev,
          [name]: eventOrData as Moment,
        }));
        dispatchFilterChange(name, eventOrData as Moment);
        break;
      }
      default: {
        const value = eventOrData?.target?.value || '';
        setLocalValues((prev) => ({
          ...prev,
          [name]: value as string,
        }));
        debouncedDispatchFilterChange(name, value as string);
      }
    }
  };

  const recordId = record.id;

  useEffect(() => {
    dispatch(
      filterChange({
        resource,
        filters: {
          fromTimestamp: time().subtract(1, 'months'),
          toTimestamp: time(),
          bankAccountId: recordId,
        },
      })
    );
    return () => {
      dispatch(cleanupFilters({ resource }));
    };
  }, [dispatch, resource, recordId]);

  return (
    <Fragment>
      {filters && (
        <div className={classes.container}>
          <div className={classes.inputsGroup}>
            <DateTimePicker
              className={classes.datePickers}
              format='L - LT'
              ampm={hasCurrentLocale12HourFormat()}
              margin='normal'
              label={t('fromTimestamp')}
              value={localValues.fromTimestamp}
              onChange={handleChange('fromTimestamp')}
            />
            <DateTimePicker
              className={classes.datePickers}
              format='L - LT'
              ampm={hasCurrentLocale12HourFormat()}
              margin='normal'
              label={t('toTimestamp')}
              value={localValues.toTimestamp}
              onChange={handleChange('toTimestamp')}
            />
          </div>
          <BackgroundTaskExport type='BANK_ACCOUNT_TRANSFER_EXPORT' queryArguments={filters} />
        </div>
      )}
    </Fragment>
  );
};

const useStyles = makeStyles((theme) => ({
  container: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
    paddingBottom: theme.spacing(1),
  },
  inputsGroup: {
    display: 'flex',
    width: '100%',
    flexWrap: 'wrap',
  },
  formControl: {
    paddingBottom: theme.spacing(1),
    marginRight: theme.spacing(1),
    width: 200,
    '&:last-child': {
      marginRight: 0,
    },
  },
  datePickers: {
    paddingBottom: theme.spacing(1),
    marginRight: theme.spacing(1),
    marginTop: 0,
    marginBottom: 0,
    width: 200,
    '& > div': {
      paddingRight: 0,
      '& > div': {
        marginLeft: 0,
      },
    },
  },
}));

export default Filters;
